import {
  Button,
  ButtonGroup,
  closeDialog,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  type FormSubmitHandler,
  Label,
  type LabelActions,
  openConfirmDialog,
  showNotification,
  useDialogRequestClose,
} from 'platform/components';
import {Box, Grid, HStack, Link, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {type ReactNode, useRef, useState} from 'react';
import type {DeepPartial, UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil, reject} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  type ContextTemplateResponseBody,
  useGetContextTemplateListQuery,
  useListContextParametersQuery,
  useListEmailCredentialsQuery,
} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {
  FINANCING_ENDING_SKIP_REASON_BY_CODE,
  type FinancingEndingPreflightResponseBody,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {getApiErrorMessages} from '@dms/api/utils';
import i18n from '@dms/i18n';
import {myProfileRoutes, settingsRoutes, testIds} from '@dms/routes';
import {
  EmailMessageUpload,
  handleApiError,
  hasMessageChanged,
  isEmailHtmlEmpty,
  refreshDmsDatagrid,
  type UploadedCustomerMessageFile,
  useEmailBodyImageUpload,
  useSmtpOptions,
} from '@dms/shared';

import {type ApiError, composePath, HTTP_STATUS_CODES, type Nullish, useOnMount} from 'shared/web';

import {FINANCING_ENDING_EMAIL_DIALOG_ID} from '../../constants/FINANCING_ENDING_EMAIL_DIALOG_ID';
import {FINANCING_ENDING_GRID_CODE} from '../../constants/FINANCING_ENDING_GRID_CODE';
import {useFinancingEndingEditablePreview} from '../../hooks/useFinancingEndingEditablePreview';
import {useSubmitFinancingEndingEditableEmail} from '../../hooks/useSubmitFinancingEndingEditableEmail';
import {
  FinancingEndingEditableEmailFormSchema,
  type FinancingEndingEditableEmailFormValues,
} from '../../utils/FinancingEndingEditableEmailFormSchema';
import {getFinancingEndingTemplateMessage} from '../../utils/getFinancingEndingTemplateMessage';
import {FinancingEndingEditableMessageFields} from '../FinancingEndingEditableMessageFields/FinancingEndingEditableMessageFields';
import {FinancingEndingPreviewNavigator} from '../FinancingEndingPreviewNavigator/FinancingEndingPreviewNavigator';
import {FinancingEndingComposeCloseGuard} from './components/FinancingEndingComposeCloseGuard';

export type FinancingEndingComposeFormProps = {
  rowIds: string[];
  preflight: FinancingEndingPreflightResponseBody;
  summary: ReactNode;
  onSubmitted: (bulkOperationId: string) => void;
};

type FormValues = FinancingEndingEditableEmailFormValues;

type MessageBaseline = {subject: string; body: string};

const TEMPLATE_LIST_ARG = {type: 'email', context: 'business-case'} as const;
const CONTEXT_PARAMETERS_ARG = {context: 'business-case'} as const;

const ROW_SKIPPED_STATUS = 422;

const rowSkippedErrorSchema = z.object({
  status: z.literal(ROW_SKIPPED_STATUS),
  data: z.object({errors: z.array(z.object({code: z.string()})).min(1)}),
});

// Only a known reason is about the recipient; any other refusal is about the text or the request.
const getSkipReasonCode = (error: ApiError | Nullish) => {
  const result = rowSkippedErrorSchema.safeParse(error);
  if (!result.success) {
    return null;
  }
  const code = result.data.data.errors[0].code;
  return isNil(FINANCING_ENDING_SKIP_REASON_BY_CODE[code]) ? null : code;
};

const getPreviewErrorMessage = (error: ApiError | Nullish) => {
  if (isNil(error) || isNotNil(getSkipReasonCode(error))) {
    return null;
  }
  const messages = getApiErrorMessages(error);
  return Array.isArray(messages) ? messages.join(' ') : messages;
};

const isDuplicateSubmit = (error: ApiError) =>
  'status' in error && error.status === HTTP_STATUS_CODES.CONFLICT;

const isMessageEmpty = (values: DeepPartial<FormValues>) =>
  (values.subject ?? '').trim() === '' && isEmailHtmlEmpty(values.body);

const isMessageIncomplete = (values: DeepPartial<FormValues>) =>
  (values.subject ?? '').trim() === '' || isEmailHtmlEmpty(values.body);

// `useOnMount` cannot run in the form's render prop, and the `RunOnMount` of `@dms/shared` is not
// exported. The `key` runs it again when the only template changes.
type RunOnMountProps = {
  onMount: () => void;
};

function RunOnMount(props: RunOnMountProps) {
  useOnMount(props.onMount);
  return null;
}

// The dialog this form is opened in; another dialog may be open above it.
const closeComposeDialog = () => closeDialog(FINANCING_ENDING_EMAIL_DIALOG_ID);

const getManageTemplatesAction = (): LabelActions[number] => ({
  title: i18n.t('entity.businessCase.financingEnding.compose.manageTemplates'),
  leftIcon: 'action/settings',
  target: '_blank',
  href: composePath(settingsRoutes.messageTemplates, {isSearchParamsPreserved: false}),
});

export function FinancingEndingComposeForm(props: FinancingEndingComposeFormProps) {
  const {defaultSmtpCredentialId, smtpCredentialsOptions, isLoading} =
    useSmtpOptions('customer-message');
  const templateList = useGetContextTemplateListQuery(TEMPLATE_LIST_ARG);
  const contextParameters = useListContextParametersQuery(CONTEXT_PARAMETERS_ARG);
  const preview = useFinancingEndingEditablePreview();
  const submission = useSubmitFinancingEndingEditableEmail();
  const upload = useEmailBodyImageUpload();
  const requestClose = useDialogRequestClose();
  const [previewIndex, setPreviewIndex] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedCustomerMessageFile[]>([]);
  const {data: mailboxes} = useListEmailCredentialsQuery({});
  const {data: currentUser} = useGetCurrentUserInfoQuery();
  // The text a prefilled template put into the fields; closing over it loses nothing.
  const baselineRef = useRef<MessageBaseline | null>(null);
  // Set after a submit without a known result; the server may have created the operation already.
  // It lives as long as this dialog: a reopened dialog sends without the question (accepted limit).
  const hasUnknownOutcomeRef = useRef(false);
  const isConfirmingResendRef = useRef(false);

  const templates = templateList.data ?? [];
  const onlyTemplate = templates.length === 1 ? templates[0] : null;
  const eligibleRows = props.preflight.rows.filter((row) => row.eligible);
  const eligibleRowIds = eligibleRows.map((row) => row.rowId);
  const previewRow = eligibleRows[previewIndex];
  const previewRowId = previewRow?.rowId ?? null;
  const userName = reject(isNilOrEmpty, [currentUser?.firstName, currentUser?.lastName]).join(' ');
  const isPreviewOfShownRow = preview.rowId === previewRowId;

  const getSender = (emailCredentialsId: string | Nullish) => {
    const mailbox = mailboxes?.credentials.find((item) => item.id === emailCredentialsId);
    return isNil(mailbox)
      ? null
      : reject(isNilOrEmpty, [userName, mailbox.emailAddress]).join(' · ');
  };

  const showRecipient = (index: number, values: DeepPartial<FormValues>) => {
    setPreviewIndex(index);
    preview.requestPreview({rowId: eligibleRowIds[index], values});
  };

  // The only template fills fields nobody wrote into; text typed while the list loaded stays.
  // A list loaded before the form opened is already in its default values.
  const applyOnlyTemplate = (
    template: ContextTemplateResponseBody,
    formApi: UseFormReturn<FormValues>
  ) => {
    const message = getFinancingEndingTemplateMessage(template);
    const values = formApi.getValues();
    const isPrefilled =
      values.templateId === message.templateId &&
      values.subject === message.subject &&
      values.body === message.body;
    if (!isPrefilled && !isMessageEmpty(values)) {
      return;
    }
    if (!isPrefilled) {
      formApi.setValue('subject', message.subject);
      formApi.setValue('body', message.body);
      formApi.setValue('templateId', message.templateId);
      formApi.setValue('locale', message.locale);
    }
    baselineRef.current = {subject: message.subject, body: message.body};
    showRecipient(previewIndex, formApi.getValues());
  };

  const confirmResendAfterUnknownOutcome = () =>
    new Promise<boolean>((resolve) => {
      isConfirmingResendRef.current = true;
      openConfirmDialog({
        text: i18n.t('entity.businessCase.financingEnding.send.outcomeUnknown'),
        onConfirm: () => resolve(true),
        onCloseComplete: () => {
          isConfirmingResendRef.current = false;
          resolve(false);
        },
        'data-testid': testIds.businessCase.financingEnding('compose-resendConfirm'),
      });
    });

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (!upload.ensureBodyReadyToSend(values.body, blockerContext)) {
      return;
    }

    if (hasUnknownOutcomeRef.current && !(await confirmResendAfterUnknownOutcome())) {
      return;
    }

    const result = await submission.submit({
      recordIds: props.rowIds,
      values,
      attachments: uploadedFiles,
    });

    match(result)
      .with({status: 'submitted'}, ({bulkOperationId}) => {
        props.onSubmitted(bulkOperationId);
        closeComposeDialog();
        refreshDmsDatagrid(FINANCING_ENDING_GRID_CODE, {deselectAll: true});
      })
      .with({status: 'prepareFailed'}, ({error}) => handleApiError(error))
      .with({status: 'submitRefused'}, ({error}) => {
        handleApiError(error);
        // The first submit of a duplicate is already running, so this dialog has nothing left to do.
        if (isDuplicateSubmit(error)) {
          closeComposeDialog();
        }
      })
      // The operation may exist already; sending again by itself could send the e-mails twice.
      .with({status: 'submitOutcomeUnknown'}, () => {
        hasUnknownOutcomeRef.current = true;
        showNotification.warning(i18n.t('entity.businessCase.financingEnding.send.outcomeUnknown'));
      })
      .otherwise(() => undefined);
  };

  // Read once, when the form opens.
  const defaultValues: DeepPartial<FormValues> = {
    emailCredentialsId: defaultSmtpCredentialId,
    ...(isNil(onlyTemplate)
      ? {subject: '', body: ''}
      : getFinancingEndingTemplateMessage(onlyTemplate)),
  };

  if (isLoading) {
    return (
      <Box data-testid={testIds.businessCase.financingEnding('compose')}>
        <Grid columns={[1, 1, 2]}>
          <VStack spacing={4}>
            {props.summary}
            <DataStatus isLoading />
          </VStack>
        </Grid>
      </Box>
    );
  }

  return (
    <Box data-testid={testIds.businessCase.financingEnding('compose')}>
      <Form<FormValues>
        defaultValues={defaultValues}
        experimentalZodSchema={FinancingEndingEditableEmailFormSchema}
        onSubmit={handleSubmit}
        onChange={(values) => preview.schedulePreview({rowId: previewRowId, values})}
      >
        {(control, formApi) => {
          const [emailCredentialsId, subject, body] = formApi.watch([
            'emailCredentialsId',
            'subject',
            'body',
          ]);

          const previewPlaceholder = match({
            hasMailbox: isNotNilOrEmpty(emailCredentialsId),
            hasMessage: !isMessageIncomplete({subject, body}),
          })
            .with({hasMailbox: false}, () =>
              i18n.t('entity.businessCase.financingEnding.preview.placeholderMailbox')
            )
            .with({hasMessage: false}, () =>
              i18n.t('entity.businessCase.financingEnding.preview.placeholderMessage')
            )
            .otherwise(() => null);

          return (
            <VStack spacing={4}>
              <FinancingEndingComposeCloseGuard
                isLocked={() => submission.isSubmitting || isConfirmingResendRef.current}
                hasUnsavedChanges={() =>
                  hasMessageChanged(
                    {
                      subject: formApi.getValues('subject'),
                      body: formApi.getValues('body'),
                      attachmentCount: uploadedFiles.length,
                    },
                    {...baselineRef.current, attachmentCount: 0},
                    true
                  )
                }
                data-testid={testIds.businessCase.financingEnding('compose')}
              />
              <Show when={onlyTemplate}>
                {(template) => (
                  <RunOnMount
                    key={template.id}
                    onMount={() => applyOnlyTemplate(template, formApi)}
                  />
                )}
              </Show>
              <Grid columns={[1, 1, 2]}>
                <VStack spacing={4}>
                  {props.summary}
                  <FormField
                    control={control}
                    name="emailCredentialsId"
                    type="choice"
                    label={i18n.t('entity.customer.labels.sender')}
                    options={smtpCredentialsOptions}
                    onChange={() => showRecipient(previewIndex, formApi.getValues())}
                    isNotClearable
                    isRequired
                    menuInPortal
                    data-testid={testIds.businessCase.financingEnding('compose-mailbox')}
                  />
                  <FinancingEndingEditableMessageFields
                    control={control}
                    formApi={formApi}
                    templates={templates}
                    isTemplatesLoading={templateList.isLoading}
                    isTemplatesError={templateList.isError}
                    parameters={contextParameters.data ?? []}
                    isLoadingParameters={contextParameters.isLoading}
                    upload={upload}
                    messageLabelActions={
                      props.preflight.canManageTemplates ? [getManageTemplatesAction()] : undefined
                    }
                    isDisabled={submission.isSubmitting}
                    onTemplateApplied={() => showRecipient(previewIndex, formApi.getValues())}
                    data-testid={testIds.businessCase.financingEnding('compose-message')}
                  />
                  <HStack justify="space-between" width="100%">
                    <Label>{i18n.t('entity.customer.labels.messageWithSignature')}</Label>
                    <Link
                      leftIcon="image/edit"
                      size="xSmall"
                      href={myProfileRoutes.emailSignature}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {i18n.t('entity.customer.labels.goToUserSignature')}
                    </Link>
                  </HStack>
                  {/* `inert` blocks clicks, keys and drops but keeps the upload state. */}
                  <div
                    inert={submission.isSubmitting}
                    data-testid={testIds.businessCase.financingEnding('compose-attachments')}
                  >
                    <VStack spacing={1}>
                      <EmailMessageUpload
                        uploadedFiles={uploadedFiles}
                        setUploadedFiles={setUploadedFiles}
                        recordId={previewRowId}
                        resourceId={EntityResourceIds.businessCase}
                      />
                      <Text size="small" color="tertiary">
                        {i18n.t('entity.businessCase.financingEnding.compose.attachmentsSource')}
                      </Text>
                    </VStack>
                  </div>
                </VStack>
                <FinancingEndingPreviewNavigator
                  index={previewIndex}
                  count={eligibleRowIds.length}
                  recipientName={previewRow?.recipientName}
                  recipientEmail={
                    (isPreviewOfShownRow ? preview.preview?.recipientEmail : null) ??
                    previewRow?.recipientEmail
                  }
                  sender={getSender(emailCredentialsId)}
                  parameters={contextParameters.data ?? []}
                  isLoading={isPreviewOfShownRow && preview.isLoading}
                  preview={isPreviewOfShownRow ? preview.preview : null}
                  skipReasonCode={isPreviewOfShownRow ? getSkipReasonCode(preview.error) : null}
                  errorMessage={isPreviewOfShownRow ? getPreviewErrorMessage(preview.error) : null}
                  placeholder={previewPlaceholder}
                  onPrevious={() => showRecipient(previewIndex - 1, formApi.getValues())}
                  onNext={() => showRecipient(previewIndex + 1, formApi.getValues())}
                />
              </Grid>
              <DialogFooter>
                <ButtonGroup align="right">
                  <Button
                    variant="secondary"
                    title={i18n.t('general.actions.cancel')}
                    onClick={requestClose}
                    isDisabled={submission.isSubmitting}
                    data-testid={testIds.businessCase.financingEnding('cancel')}
                  />
                  <FormButton
                    control={control}
                    type="submit"
                    title={i18n.t('entity.businessCase.financingEnding.send.submit', {
                      count: props.preflight.plannedCount,
                    })}
                    data-testid={testIds.businessCase.financingEnding('send')}
                  />
                </ButtonGroup>
              </DialogFooter>
            </VStack>
          );
        }}
      </Form>
    </Box>
  );
}
